import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadSimple, FileText, Sparkle, Calculator, Trash, ArrowLeft, CheckCircle, FilePdf, House, Files, Highlighter, ListChecks, ClockCounterClockwise, PencilSimple, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";
import { StatusBadge } from "./Dashboard";
import A4Sheet from "@/components/A4Sheet";
import { Switch } from "@/components/ui/switch";
import AktenVerlauf from "@/components/AktenVerlauf";
import SchriftsatzEditor, { WordButton } from "@/components/SchriftsatzEditor";
import PdfViewer from "@/components/PdfViewer";
import CaptureSteps from "@/components/CaptureSteps";
import PortalPanel, { FreigabeSwitch } from "@/components/PortalPanel";
import { DeviceMobile } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const euro = (n) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);

export default function AkteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [akte, setAkte] = useState(null);
  const [docs, setDocs] = useState([]);
  const [schriftsaetze, setSchriftsaetze] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editingSchrift, setEditingSchrift] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [schriftTyp, setSchriftTyp] = useState("anspruchsschreiben");
  const [zusatz, setZusatz] = useState("");
  const [previewSchrift, setPreviewSchrift] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "overview");
  const [captureMode, setCaptureMode] = useState(searchParams.get("capture") === "1");

  const [bezForm, setBezForm] = useState({
    reparaturkosten: 0, wertminderung: 0, wiederbeschaffungswert: 0,
    restwert: 0, nutzungsausfall_tage: 0, fahrzeugklasse: "D", sachverstaendigenkosten: 0,
  });

  // Scanner correction state
  const [scannerDoc, setScannerDoc] = useState(null);
  const [scannerData, setScannerData] = useState(null);
  const [aussteller, setAussteller] = useState(null);

  // PDF Viewer state
  const [viewerDoc, setViewerDoc] = useState(null);
  const [markers, setMarkers] = useState([]);

  const loadMarkers = useCallback(async () => {
    try {
      const r = await api.get(`/marker?akte_id=${id}`);
      setMarkers(r.data);
    } catch {}
  }, [id]);

  useEffect(() => { loadMarkers(); }, [loadMarkers]);

  const load = useCallback(async (opts = {}) => {
    const silent = opts && opts.silent === true;
    console.log("[AkteDetail] load() called, silent=", silent);
    if (!silent) setLoading(true);
    setLoadError(null);
    try {
      const [a, d, s] = await Promise.all([
        api.get(`/akten/${id}`),
        api.get(`/dokumente?akte_id=${id}`),
        api.get(`/schriftsaetze?akte_id=${id}`),
      ]);
      console.log("[AkteDetail] loaded akte mandant_id:", a.data.mandant_id, "updated_at:", a.data.updated_at);
      setAkte(a.data);
      setDocs(d.data);
      setSchriftsaetze(s.data);
      if (a.data.bezifferung) {
        setBezForm(f => ({ ...f, ...a.data.bezifferung, fahrzeugklasse: a.data.bezifferung?.fahrzeugklasse || "D", schadenart: a.data.bezifferung?.schadenart_quelle === "manuell" ? a.data.bezifferung.schadenart : "auto" }));
      }
    } catch (err) {
      console.error("[AkteDetail] load() error:", err?.response?.status, err?.message);
      setLoadError({ status: err?.response?.status, message: errMsg(err, "Akte konnte nicht geladen werden") });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const uploadFile = async (file) => {
    const form = new FormData();
    form.append("file", file);
    form.append("akte_id", id);
    form.append("kategorie", "gutachten");
    try {
      const r = await api.post("/dokumente/upload", form);
      toast.success("Dokument hochgeladen");
      await load();
      // Auto-analyze
      await analyze(r.data.id);
    } catch (err) {
      toast.error("Upload fehlgeschlagen");
    }
  };

  const analyze = async (dokId) => {
    setAnalyzing(true);
    const tId = toast.loading("Gutachten wird analysiert...");
    try {
      const r = await api.post(`/dokumente/${dokId}/analyze`);
      const extracted = r.data.extracted_data;
      toast.dismiss(tId);
      toast.success(`Analyse fertig${r.data.aussteller ? ` · Aussteller: ${r.data.aussteller}` : ""}`);
      // Show correction UI
      setScannerDoc(dokId);
      setScannerData(extracted);
      setAussteller(r.data.aussteller || null);
      // Apply to akte (best-effort)
      await api.post(`/akten/${id}/apply-gutachten`, extracted);
      await load();
    } catch (err) {
      toast.dismiss(tId);
      toast.error(err?.response?.data?.detail || "Analyse fehlgeschlagen");
    } finally {
      setAnalyzing(false);
    }
  };

  const saveCorrections = async () => {
    if (!scannerDoc || !scannerData) return;
    const tId = toast.loading("Korrekturen werden gespeichert & gelernt...");
    try {
      const corrections = Object.entries(scannerData).map(([feld, neuer_wert]) => ({ feld, neuer_wert }));
      await api.post(`/dokumente/${scannerDoc}/correct`, { corrections });
      await api.post(`/akten/${id}/apply-gutachten`, scannerData);
      toast.dismiss(tId);
      toast.success("Korrekturen gelernt — wird beim nächsten Mal genauer");
      setScannerDoc(null);
      setScannerData(null);
      await load();
    } catch (err) {
      toast.dismiss(tId);
      toast.error("Fehler beim Speichern");
    }
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    for (const f of files) {
      await uploadFile(f);
    }
  };

  const recomputeBez = async () => {
    try {
      const payload = { ...bezForm, schadenart: bezForm.schadenart && bezForm.schadenart !== "auto" ? bezForm.schadenart : null };
      const r = await api.post(`/akten/${id}/bezifferung`, payload);
      toast.success("Bezifferung aktualisiert");
      setAkte(a => ({ ...a, bezifferung: { ...(a?.bezifferung || {}), ...r.data } }));
    } catch (err) {
      toast.error(errMsg(err, "Bezifferung konnte nicht gespeichert werden"));
    }
  };

  const generateSchrift = async () => {
    setGenerating(true);
    const tId = toast.loading("Schriftsatz wird erstellt...");
    try {
      const r = await api.post("/schriftsaetze/generate", {
        akte_id: id, typ: schriftTyp, zusatz_info: zusatz,
      });
      toast.dismiss(tId);
      toast.success("Schriftsatz generiert");
      setPreviewSchrift(r.data);
      setZusatz("");
      await load();
    } catch (err) {
      toast.dismiss(tId);
      toast.error("Generierung fehlgeschlagen");
    } finally {
      setGenerating(false);
    }
  };

  const updateStatus = async (status) => {
    await api.patch(`/akten/${id}`, { status });
    toast.success("Status aktualisiert");
    load();
  };

  const deleteDoc = async (dokId) => {
    await api.delete(`/dokumente/${dokId}`);
    toast.success("Dokument gelöscht");
    load();
  };

  const deleteAkte = async () => {
    if (!window.confirm("Akte wirklich löschen?")) return;
    await api.delete(`/akten/${id}`);
    toast.success("Akte gelöscht");
    nav("/app/akten");
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="akte-detail-loading">
      <div className="flex flex-col items-center gap-3 text-[#6B7280]">
        <div className="h-10 w-10 rounded-full border-2 border-[#1A2238] border-t-transparent animate-spin" />
        <p className="text-sm font-medium">Akte wird geladen …</p>
      </div>
    </div>
  );
  if (!akte) return (
    <div className="max-w-lg mx-auto mt-12 bg-white border border-[#E5E7EB] rounded-lg p-8 text-center space-y-4" data-testid="akte-load-error">
      <p className="overline text-[#D32F2F]">{loadError?.status === 404 ? "Nicht gefunden" : "Fehler beim Laden"}</p>
      <h2 className="font-serif text-2xl">
        {loadError?.status === 404 ? "Diese Akte existiert nicht (mehr)" : "Akte konnte nicht geladen werden"}
      </h2>
      <p className="text-sm text-[#6B7280]" data-testid="akte-load-error-detail">
        {loadError?.status === 404
          ? "Möglicherweise wurde sie gelöscht oder gehört zu einem anderen Zugang."
          : (loadError?.message || "Unbekannter Fehler")}
      </p>
      <div className="flex justify-center gap-2">
        <Button variant="outline" onClick={() => nav("/app/akten")} data-testid="akte-load-error-back">Zur Aktenliste</Button>
        {loadError?.status !== 404 && (
          <Button className="bg-[#1A2238] hover:bg-[#2C3859]" onClick={() => load()} data-testid="akte-load-error-retry">Erneut versuchen</Button>
        )}
      </div>
    </div>
  );

  const bez = akte.bezifferung;

  return (
    <div className="space-y-8" data-testid="akte-detail">
      <div>
        <Button variant="ghost" size="sm" asChild className="-ml-3">
          <Link to="/app/akten" data-testid="back-to-akten"><ArrowLeft size={14} className="mr-1" /> Zurück</Link>
        </Button>
      </div>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="overline text-[#D32F2F] mb-2 font-mono">{akte.aktenzeichen}</p>
          <h1 className="font-serif text-4xl tracking-tight">{akte.mandant_name || "Ohne Mandant"}</h1>
          <p className="text-sm text-[#4B5563] mt-2">
            {akte.unfalldatum ? new Date(akte.unfalldatum).toLocaleDateString("de-DE") : "—"}
            {akte.unfallort ? ` · ${akte.unfallort}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant={captureMode ? "default" : "outline"}
            size="sm"
            onClick={() => {
              const next = !captureMode;
              setCaptureMode(next);
              if (next) {
                searchParams.set("capture", "1");
              } else {
                searchParams.delete("capture");
              }
              setSearchParams(searchParams, { replace: true });
            }}
            className={captureMode ? "bg-[#1A2238] hover:bg-[#2C3859]" : ""}
            data-testid="toggle-capture-btn"
          >
            <ListChecks size={14} className="mr-1" /> Erfassungs-Modus
          </Button>
          <Select value={akte.status} onValueChange={updateStatus}>
            <SelectTrigger className="w-44" data-testid="akte-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="eingang">Eingang</SelectItem>
              <SelectItem value="analyse">Analyse</SelectItem>
              <SelectItem value="aktion">Aktion</SelectItem>
              <SelectItem value="schreiben">Schreiben</SelectItem>
              <SelectItem value="versand">Versand</SelectItem>
              <SelectItem value="fristen">Frist läuft</SelectItem>
              <SelectItem value="verfahren">Verfahren</SelectItem>
              <SelectItem value="abschluss">Abschluss</SelectItem>
            </SelectContent>
          </Select>
          <StatusBadge status={akte.status} />
          <label className="inline-flex items-center gap-2 text-xs text-[#4B5563] border border-[#E5E7EB] rounded-md px-2.5 h-9 bg-white" title="Automatische Wiedervorlagen nach Versand für diese Akte" data-testid="akte-wv-auto-wrap">
            <BellRinging size={14} weight="duotone" className="text-[#F59E0B]" />
            <span className="hidden sm:inline">Auto-Wiedervorlage</span>
            <Switch
              checked={akte.wiedervorlage_auto == null ? user?.wiedervorlage_auto !== false : !!akte.wiedervorlage_auto}
              onCheckedChange={async (v) => {
                try {
                  const r = await api.post(`/akten/${id}/wiedervorlage-auto`, { aktiv: v });
                  setAkte(a => ({ ...a, wiedervorlage_auto: v }));
                  toast.success(v ? "Automatische Wiedervorlagen aktiv" : `Automatische Wiedervorlagen aus${r.data.geschlossen ? ` · ${r.data.geschlossen} offene geschlossen` : ""}`);
                } catch (err) { toast.error(errMsg(err, "Einstellung fehlgeschlagen")); }
              }}
              data-testid="akte-wv-auto-switch"
            />
          </label>
          <Button variant="outline" size="sm" onClick={deleteAkte} data-testid="delete-akte-btn"><Trash size={14} /></Button>
        </div>
      </header>

      {captureMode ? (
        <CaptureSteps
          akte={akte}
          docs={docs}
          schriftsaetze={schriftsaetze}
          onUpdated={(opts) => {
            // Silent reload: keine "Lädt …"-Vollbildanzeige während Hintergrund-Tasks laufen
            load({ silent: true });
            if (opts?.openTab) {
              setActiveTab(opts.openTab);
              setCaptureMode(false);
              searchParams.delete("capture");
              setSearchParams(searchParams, { replace: true });
            }
          }}
          onCloseCapture={() => {
            setCaptureMode(false);
            searchParams.delete("capture");
            setSearchParams(searchParams, { replace: true });
          }}
        />
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-[180px_1fr] gap-10">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-8 lg:self-start">
          <nav className="space-y-1">
            {[
              { key: "overview", label: "Übersicht", icon: House, testid: "tab-overview" },
              { key: "dokumente", label: "Dokumente", icon: Files, testid: "tab-dokumente", count: docs.length },
              { key: "bezifferung", label: "Bezifferung", icon: Calculator, testid: "tab-bezifferung" },
              { key: "schriftsaetze", label: "Schriftsätze", icon: FileText, testid: "tab-schriftsaetze", count: schriftsaetze.length },
              { key: "verlauf", label: "Verlauf", icon: ClockCounterClockwise, testid: "tab-verlauf" },
              { key: "portal", label: "Mandantenportal", icon: DeviceMobile, testid: "tab-portal", count: docs.filter(d => d.mandant_sichtbar).length },
            ].map(({ key, label, icon: Icon, testid, count }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                data-testid={testid}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  activeTab === key
                    ? "bg-[#1A2238] text-white"
                    : "text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0A192F]"
                }`}
              >
                <span className="flex items-center gap-3">
                  <Icon size={18} weight="duotone" />
                  {label}
                </span>
                {typeof count === "number" && count > 0 && (
                  <span className={`text-xs font-mono px-2 py-0.5 rounded ${activeTab === key ? "bg-white/15 text-white" : "bg-[#F3F4F6] text-[#4B5563]"}`}>
                    {count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <div className="min-w-0 space-y-6">
          {/* OVERVIEW */}
          {activeTab === "overview" && (
            <div className="space-y-8">
              {bez && (
                <div className="bg-[#1A2238] text-white rounded-lg p-10 relative overflow-hidden">
                  <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[#D32F2F]/10 blur-3xl pointer-events-none" />
                  <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 items-end">
                    <div className="md:col-span-2">
                      <p className="overline text-[#D32F2F] mb-4">Streitwert</p>
                      <p className="font-serif text-6xl xl:text-7xl tracking-tight leading-none" data-testid="overview-streitwert">{euro(bez.streitwert)}</p>
                      <p className="text-sm text-white/60 mt-4">Gesamtschaden inkl. aller Positionen</p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-1 gap-4 md:border-l md:border-white/15 md:pl-8">
                      <div>
                        <p className="overline text-white/50 mb-1">RVG-Gebühr</p>
                        <p className="font-mono text-xl">{euro(bez.rvg_gebuehr)}</p>
                      </div>
                      <div>
                        <p className="overline text-white/50 mb-1">Nutzungsausfall</p>
                        <p className="font-mono text-xl">{euro(bez.nutzungsausfall_gesamt)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <InfoCard
                  title="Gegner"
                  accent="#D32F2F"
                  kicker="Gegnerische Partei"
                  data={akte.gegner || {}}
                  fields={[
                    ["name", "Name", true],
                    ["kennzeichen", "Kennzeichen", false, "mono"],
                    ["versicherung", "Versicherung", true],
                    ["versicherungsscheinnr", "VS-Schein-Nr.", false, "mono"],
                    ["schadennr", "Schadennummer", false, "mono"],
                  ]}
                />
                <InfoCard
                  title="Fahrzeug"
                  accent="#1A2238"
                  kicker="Mandantenfahrzeug"
                  data={akte.fahrzeug || {}}
                  fields={[
                    ["marke", "Marke", true],
                    ["modell", "Modell", true],
                    ["kennzeichen", "Kennzeichen", false, "mono"],
                    ["erstzulassung", "Erstzulassung", false, "mono"],
                    ["laufleistung", "Laufleistung (km)", false, "mono"],
                  ]}
                />
              </div>

              {akte.unfallhergang && (
                <div className="bg-white border border-[#E5E7EB] rounded-lg p-8">
                  <p className="overline text-[#D32F2F] mb-3">Unfallhergang</p>
                  <p className="font-serif text-xl leading-relaxed text-[#0A192F]">{akte.unfallhergang}</p>
                </div>
              )}
            </div>
          )}

          {/* DOKUMENTE */}
          {activeTab === "dokumente" && (
            <div className="space-y-6">
              {/* Scanner correction panel */}
              {scannerDoc && scannerData && (
                <ScannerCorrection
                  data={scannerData}
                  aussteller={aussteller}
                  onChange={setScannerData}
                  onSave={saveCorrections}
                  onDismiss={() => { setScannerDoc(null); setScannerData(null); }}
                />
              )}

              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`dropzone ${dragOver ? "dropzone--active" : ""} rounded-lg p-12 text-center cursor-pointer`}
                data-testid="dropzone"
              >
                <UploadSimple size={40} weight="duotone" className="mx-auto text-[#1A2238]" />
            <p className="font-serif text-2xl mt-4">Gutachten hier ablegen</p>
            <p className="text-sm text-[#4B5563] mt-2">oder Datei auswählen — PDF, PNG, JPG</p>
            <label className="inline-block mt-5">
              <input type="file" accept="application/pdf,image/*" className="hidden" data-testid="file-input" onChange={e => {
                const f = e.target.files?.[0];
                if (f) uploadFile(f);
              }} />
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#1A2238] text-white text-sm cursor-pointer hover:bg-[#2C3859]">
                <UploadSimple size={16} /> Datei auswählen
              </span>
            </label>
          </div>

          <div className="space-y-2">
            {docs.length === 0 ? (
              <p className="text-sm text-[#4B5563]">Noch keine Dokumente hochgeladen.</p>
            ) : docs.map(d => {
              const docMarkers = markers.filter(m => m.dokument_id === d.id);
              return (
                <div key={d.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex items-center justify-between gap-4 hover:border-[#1A2238] transition-colors" data-testid={`doc-row-${d.id}`}>
                  <button
                    type="button"
                    onClick={() => setViewerDoc(d)}
                    className="flex items-center gap-3 min-w-0 flex-1 text-left cursor-pointer"
                    data-testid={`open-doc-${d.id}`}
                  >
                    <FilePdf size={28} weight="duotone" className="text-[#D32F2F] shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium truncate hover:text-[#D32F2F] transition-colors">{d.original_filename}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-[#4B5563]">{(d.size / 1024).toFixed(0)} KB · {new Date(d.created_at).toLocaleString("de-DE")}</span>
                        {docMarkers.length > 0 && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-[#D32F2F]">
                            <Highlighter size={11} weight="duotone" /> {docMarkers.length} Markierung{docMarkers.length === 1 ? "" : "en"}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-2 shrink-0">
                    <FreigabeSwitch doc={d} compact onChanged={() => load({ silent: true })} />
                    {d.analyzed ? (
                      <span className="text-xs text-[#16A34A] flex items-center gap-1"><CheckCircle size={14} /> Analysiert</span>
                    ) : (
                      <Button variant="outline" size="sm" disabled={analyzing} onClick={(e) => { e.stopPropagation(); analyze(d.id); }} data-testid={`analyze-${d.id}`}>
                        <Sparkle size={14} className="mr-1" /> Analysieren
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteDoc(d.id); }} data-testid={`delete-doc-${d.id}`}><Trash size={14} /></Button>
                  </div>
                </div>
              );
            })}
          </div>
            </div>
          )}

        {/* BEZIFFERUNG */}
        {activeTab === "bezifferung" && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-4">
              <h3 className="font-serif text-2xl">Schadenspositionen</h3>
              {[
                ["reparaturkosten", "Reparaturkosten (€)", "number"],
                ["wertminderung", "Wertminderung (€)", "number"],
                ["wiederbeschaffungswert", "Wiederbeschaffungswert (€)", "number"],
                ["restwert", "Restwert (€)", "number"],
                ["nutzungsausfall_tage", "Nutzungsausfall (Tage)", "number"],
                ["sachverstaendigenkosten", "Sachverständigenkosten (€)", "number"],
              ].map(([k, l]) => (
                <div key={k}>
                  <Label className="overline">{l}</Label>
                  <Input type="number" value={bezForm[k]} onChange={e => setBezForm(f => ({ ...f, [k]: parseFloat(e.target.value) || 0 }))} data-testid={`bez-${k}`} />
                </div>
              ))}
              <div>
                <Label className="overline">Fahrzeugklasse (Nutzungsausfall)</Label>
                <Select value={bezForm.fahrzeugklasse} onValueChange={v => setBezForm(f => ({ ...f, fahrzeugklasse: v }))}>
                  <SelectTrigger data-testid="bez-fahrzeugklasse"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["A", "B", "C", "D", "E", "F", "G", "H", "J", "K", "L"].map(g => (
                      <SelectItem key={g} value={g}>Gruppe {g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Schadenart</Label>
                <Select value={bezForm.schadenart || "auto"} onValueChange={v => setBezForm(f => ({ ...f, schadenart: v }))}>
                  <SelectTrigger data-testid="bez-schadenart"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automatisch (Gutachten / 130 %-Regel)</SelectItem>
                    <SelectItem value="reparaturschaden">Reparaturschaden</SelectItem>
                    <SelectItem value="130_prozent">130 %-Fall</SelectItem>
                    <SelectItem value="totalschaden">Wirtschaftlicher Totalschaden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={recomputeBez} className="w-full bg-[#1A2238] hover:bg-[#2C3859]" data-testid="bez-compute-btn">
                <Calculator size={16} className="mr-2" /> Bezifferung berechnen
              </Button>
            </div>
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-3">
              <h3 className="font-serif text-2xl mb-3">Ergebnis</h3>
              {bez ? (
                <>
                  {bez.schadenart && (
                    <div className="mb-3 space-y-1" data-testid="akte-schadenart">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-medium ${
                        bez.schadenart === "totalschaden" ? "bg-[#D32F2F]/10 text-[#B91C1C] border-[#D32F2F]/30"
                        : bez.schadenart === "130_prozent" ? "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30"
                        : "bg-[#16A34A]/10 text-[#15803D] border-[#16A34A]/30"}`}>
                        {bez.schadenart === "totalschaden" ? "Wirtschaftlicher Totalschaden" : bez.schadenart === "130_prozent" ? "130 %-Fall" : "Reparaturschaden"}
                        {bez.schadenart_quelle && <span className="opacity-70 font-normal ml-1">· {({ gutachten: "laut Gutachten", manuell: "manuell", berechnet: "automatisch" })[bez.schadenart_quelle]}</span>}
                      </span>
                      {bez.schadenart_hinweis && <p className="text-xs text-[#6B7280] leading-relaxed">{bez.schadenart_hinweis}</p>}
                    </div>
                  )}
                  {bez.schadenart === "totalschaden" ? (
                    <>
                      <Row k="Wiederbeschaffungswert" v={euro(bez.wiederbeschaffungswert)} />
                      <Row k="Restwert (abzgl.)" v={`− ${euro(bez.restwert)}`} />
                      <Row k="Wiederbeschaffungsaufwand" v={euro(bez.wiederbeschaffungsaufwand ?? bez.schaden_fahrzeug)} />
                      <Row k="Wertminderung (entfällt)" v={euro(0)} />
                    </>
                  ) : (
                    <>
                      <Row k="Reparaturkosten" v={euro(bez.reparaturkosten)} />
                      <Row k="Wertminderung" v={euro(bez.wertminderung)} />
                    </>
                  )}
                  <Row k={`Nutzungsausfall (${bez.nutzungsausfall_tage} Tage × ${euro(bez.nutzungsausfall_satz)})`} v={euro(bez.nutzungsausfall_gesamt)} />
                  <Row k="Sachverständigenkosten" v={euro(bez.sachverstaendigenkosten)} />
                  <Row k="Unkostenpauschale" v={euro(bez.unkostenpauschale)} />
                  {(bez.weitere_positionen || []).map((p, i) => (
                    <Row key={p.id || i} k={p.bezeichnung} v={euro(p.betrag)} />
                  ))}
                  <div className="border-t border-[#E5E7EB] pt-3 mt-3">
                    <Row k="Streitwert / Schadensumme" v={euro(bez.streitwert)} bold />
                    <Row k="RVG-Gebühr (1,3) inkl. USt" v={euro(bez.rvg_gebuehr)} highlight />
                  </div>
                </>
              ) : <p className="text-sm text-[#4B5563]">Noch keine Bezifferung berechnet.</p>}
            </div>
          </div>
        )}

        {/* SCHRIFTSAETZE */}
        {activeTab === "schriftsaetze" && (
          <div className="space-y-6">
          {markers.length > 0 && (
            <div className="bg-[#FEF7E6] border border-[#F59E0B]/30 rounded-lg p-5" data-testid="markers-summary">
              <div className="flex items-start gap-3">
                <Highlighter size={20} weight="duotone" className="text-[#D32F2F] mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-[#0A192F] mb-1">{markers.length} Markierung{markers.length === 1 ? "" : "en"} aus dem Gutachten werden im Schriftsatz berücksichtigt</p>
                  <p className="text-xs text-[#4B5563] mb-3">Markierte Passagen werden sinngemäß bzw. wörtlich in den passenden Abschnitt aufgenommen.</p>
                  <div className="flex flex-wrap gap-1.5">
                    {markers.slice(0, 6).map(m => (
                      <span key={m.id} className="text-[11px] px-2 py-0.5 bg-white border border-[#E5E7EB] rounded font-mono">
                        {m.text.length > 50 ? m.text.slice(0, 50) + "…" : m.text}
                      </span>
                    ))}
                    {markers.length > 6 && <span className="text-[11px] text-[#4B5563]">+{markers.length - 6} weitere</span>}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
            <h3 className="font-serif text-2xl mb-4">Neuen Schriftsatz generieren</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="overline">Typ</Label>
                <Select value={schriftTyp} onValueChange={setSchriftTyp}>
                  <SelectTrigger data-testid="schrift-typ-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bezifferungsschreiben">Bezifferungsschreiben</SelectItem>
                    <SelectItem value="anspruchsschreiben">Anspruchsschreiben</SelectItem>
                    <SelectItem value="unfallhergang">Unfallhergang</SelectItem>
                    <SelectItem value="klageschrift">Klageschrift</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="overline">Zusatzinfo (optional)</Label>
                <Input value={zusatz} onChange={e => setZusatz(e.target.value)} placeholder="z.B. 100% Haftung, Bitte mit Frist 14 Tage" data-testid="schrift-zusatz-input" />
              </div>
            </div>
            <Button disabled={generating} onClick={generateSchrift} className="mt-4 bg-[#1A2238] hover:bg-[#2C3859]" data-testid="generate-schrift-btn">
              <Sparkle size={16} className="mr-2" /> {generating ? "Wird erstellt..." : "Schriftsatz generieren"}
            </Button>
          </div>

          {previewSchrift && (
            <div data-testid="schrift-preview">
              <p className="overline text-[#D32F2F] mb-3">Frisch generiert · {previewSchrift.typ.replace("_", " ")}</p>
              <A4Sheet
                content={previewSchrift.content}
                user={user}
                typ={previewSchrift.typ}
                createdAt={previewSchrift.created_at}
                meta={{
                  aktenzeichen: akte.aktenzeichen,
                  schadennr: akte.gegner?.schadennr,
                  empfaenger: akte.gegner?.versicherung
                    ? `${akte.gegner.versicherung}\n– Schadenabteilung –`
                    : null,
                }}
              />
            </div>
          )}

          <div className="space-y-3">
            <h3 className="font-serif text-2xl">Archiv</h3>
            {schriftsaetze.length === 0 ? (
              <p className="text-sm text-[#4B5563]">Noch keine Schriftsätze.</p>
            ) : schriftsaetze.map(s => (
              <details key={s.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4">
                <summary className="cursor-pointer flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText size={18} weight="duotone" />
                    <span className="font-medium capitalize">{s.typ.replace("_", " ")}</span>
                    <span className="text-xs text-[#4B5563]">{new Date(s.created_at).toLocaleString("de-DE")}</span>
                  </span>
                </summary>
                <div className="mt-4">
                  {editingSchrift === s.id ? (
                    <SchriftsatzEditor
                      schriftsatz={s}
                      akte={akte}
                      user={user}
                      onCancel={() => setEditingSchrift(null)}
                      onSaved={() => { setEditingSchrift(null); load({ silent: true }); }}
                    />
                  ) : (
                  <A4Sheet
                    content={s.content}
                    user={user}
                    typ={s.typ}
                    createdAt={s.created_at}
                    actions={(
                      <>
                        <Button variant="outline" size="sm" onClick={() => setEditingSchrift(s.id)} data-testid={`schrift-edit-btn-${s.typ}`}>
                          <PencilSimple size={14} className="mr-1" /> Bearbeiten
                        </Button>
                        <WordButton schriftsatz={s} />
                      </>
                    )}
                    meta={{
                      aktenzeichen: akte.aktenzeichen,
                      schadennr: akte.gegner?.schadennr,
                      empfaenger: akte.gegner?.versicherung
                        ? `${akte.gegner.versicherung}\n– Schadenabteilung –`
                        : null,
                    }}
                  />
                  )}
                </div>
              </details>
            ))}
          </div>
          </div>
        )}

        {activeTab === "verlauf" && (
          <div className="space-y-4" data-testid="tab-verlauf-content">
            <div>
              <p className="overline text-[#D32F2F] mb-1">Aktenverlauf</p>
              <h3 className="font-serif text-2xl">Was ist wann passiert?</h3>
            </div>
            <AktenVerlauf akteId={id} />
          </div>
        )}

        {activeTab === "portal" && (
          <div data-testid="tab-portal-content">
            <PortalPanel akteId={id} docs={docs} onDocsChanged={() => load({ silent: true })} />
          </div>
        )}
        </div>
      </div>
      )}

      {/* PDF Viewer Modal */}
      <PdfViewer
        open={!!viewerDoc}
        onClose={() => { setViewerDoc(null); loadMarkers(); }}
        dokumentId={viewerDoc?.id}
        akteId={id}
        dokumentName={viewerDoc?.original_filename}
      />
    </div>
  );
}

function ScannerCorrection({ data, aussteller, onChange, onSave, onDismiss }) {
  const FIELD_GROUPS = [
    { title: "Fahrzeug", fields: [
      ["fahrzeug_marke", "Marke"],
      ["fahrzeug_modell", "Modell"],
      ["kennzeichen", "Kennzeichen"],
      ["fahrgestellnummer", "FIN / VIN"],
      ["erstzulassung", "Erstzulassung"],
      ["laufleistung", "Laufleistung (km)", "number"],
    ]},
    { title: "Schadenshöhe", fields: [
      ["reparaturkosten_netto", "Reparaturkosten netto (€)", "number"],
      ["reparaturkosten_brutto", "Reparaturkosten brutto (€)", "number"],
      ["wertminderung", "Wertminderung (€)", "number"],
      ["wiederbeschaffungswert_netto", "WBW netto (€)", "number"],
      ["restwert", "Restwert (€)", "number"],
      ["nutzungsausfall_tage", "Nutzungsausfall (Tage)", "number"],
      ["nutzungsausfall_gruppe", "Fahrzeuggruppe (A-L)"],
    ]},
    { title: "Sachverständiger", fields: [
      ["sv_name", "Sachverständigenbüro"],
      ["sv_rechnungsnr", "Rechnungs-Nr."],
      ["sachverstaendigenkosten_netto", "SV-Kosten netto (€)", "number"],
      ["sachverstaendigenkosten_brutto", "SV-Kosten brutto (€)", "number"],
      ["sv_iban", "IBAN SV"],
      ["sv_bic", "BIC SV"],
    ]},
    { title: "Unfall & Beteiligte", fields: [
      ["unfalldatum", "Unfalldatum"],
      ["unfallort", "Unfallort"],
      ["geschaedigter_name", "Geschädigter"],
      ["verursacher_name", "Verursacher"],
      ["versicherung_gegner", "Versicherung Gegner"],
      ["schadennummer", "Schadennummer"],
    ]},
  ];
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="bg-white border-2 border-[#1A2238] rounded-lg p-6 space-y-5" data-testid="scanner-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="overline text-[#D32F2F] mb-1">Scanner</p>
          <h3 className="font-serif text-2xl">Extrahierte Daten überprüfen</h3>
          <p className="text-sm text-[#4B5563] mt-1">
            Korrigieren Sie falsche Werte direkt — Korrekturen werden gelernt
            {aussteller ? <> und für künftige Gutachten von <strong className="text-[#0A192F]">{aussteller}</strong> übernommen.</> : "."}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onDismiss} data-testid="scanner-dismiss">✕</Button>
      </div>

      {FIELD_GROUPS.map(g => (
        <div key={g.title}>
          <p className="overline text-[#9CA3AF] mb-2">{g.title}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {g.fields.map(([k, label, type]) => (
              <div key={k}>
                <Label className="text-xs text-[#4B5563]">{label}</Label>
                <Input
                  type={type === "number" ? "number" : "text"}
                  value={data[k] ?? ""}
                  onChange={e => set(k, type === "number" ? (parseFloat(e.target.value) || 0) : e.target.value)}
                  className="mt-1"
                  data-testid={`scanner-${k}`}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      <div>
        <p className="overline text-[#9CA3AF] mb-2">Schadenbeschreibung</p>
        <Textarea
          rows={3}
          value={data.schadenbeschreibung || ""}
          onChange={e => set("schadenbeschreibung", e.target.value)}
          placeholder="Was wurde wie beschädigt?"
          data-testid="scanner-schadenbeschreibung"
        />
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E5E7EB]">
        <Button variant="outline" onClick={onDismiss} data-testid="scanner-cancel">Abbrechen</Button>
        <Button onClick={onSave} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="scanner-save">
          <Sparkle size={14} className="mr-2" /> Übernehmen &amp; lernen
        </Button>
      </div>
    </div>
  );
}

function Row({ k, v, bold, highlight }) {
  return (
    <div className={`flex justify-between py-1.5 text-sm ${bold ? "font-medium" : ""}`}>
      <span className="text-[#4B5563]">{k}</span>
      <span className={`font-mono ${highlight ? "text-[#D32F2F] font-semibold" : ""}`}>{v}</span>
    </div>
  );
}

function InfoCard({ title, kicker, accent = "#1A2238", data, fields }) {
  const hasData = fields.some(([k]) => data[k] !== undefined && data[k] !== null && data[k] !== "");
  return (
    <div className="group bg-white border border-[#E5E7EB] rounded-lg overflow-hidden transition-all hover:shadow-sm">
      <div className="flex items-center gap-4 px-8 py-6 border-b border-[#E5E7EB]" style={{ borderLeft: `3px solid ${accent}` }}>
        <div>
          {kicker && <p className="overline text-[#9CA3AF] mb-1">{kicker}</p>}
          <h3 className="font-serif text-3xl tracking-tight leading-none">{title}</h3>
        </div>
      </div>
      {hasData ? (
        <dl className="divide-y divide-[#F3F4F6]">
          {fields.map(([k, l, primary, style]) => {
            const v = data[k];
            const display = v !== undefined && v !== null && v !== "" ? v : "—";
            const isEmpty = display === "—";
            return (
              <div key={k} className="grid grid-cols-5 items-baseline px-8 py-4 gap-6 hover:bg-[#FAFAFA] transition-colors">
                <dt className="col-span-2 overline text-[#9CA3AF]">{l}</dt>
                <dd className={`col-span-3 ${isEmpty ? "text-[#D1D5DB]" : "text-[#0A192F]"} ${style === "mono" ? "font-mono text-sm" : primary ? "font-serif text-xl" : "text-base"}`}>
                  {display}
                </dd>
              </div>
            );
          })}
        </dl>
      ) : (
        <div className="px-8 py-10 text-center">
          <p className="text-sm text-[#9CA3AF]">Noch keine Daten — Gutachten hochladen, damit diese Felder automatisch befüllt werden.</p>
        </div>
      )}
    </div>
  );
}
